import { useEffect, useState } from 'react';
import { Alert, Box, Checkbox, InputAdornment, TextField, Typography } from '@mui/material';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import SearchIcon from '@mui/icons-material/Search';
import { css } from '@emotion/react';

import { IWizardFile } from '../models/interfaces/IWizardFile';
import { QueueType } from '../models/enums/QueueType';
import { FileGroup } from '../queueType/QueueTypeHandler';
import { getQueueTypeHandler } from '../queueType/QueueTypeRegistry';

const containerStyle = css({
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    boxSizing: 'border-box',
    padding: 24,
    gap: 16,
    '& .confirm-summary': {
        flexShrink: 0,
        display: 'flex',
        gap: 24,
        padding: '12px 20px',
        borderRadius: 10,
        border: '1px solid #e0e0e0',
        backgroundColor: '#f8f9fa',
        alignItems: 'center',
    },
    '& .confirm-summary-stat': {
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
    },
    '& .confirm-list': {
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        paddingRight: 2,
    },
    '& .confirm-file-card': {
        flexShrink: 0,
        padding: '12px 16px',
        borderRadius: 8,
        border: '1px solid #e0e0e0',
        backgroundColor: '#ffffff',
        transition: 'box-shadow 0.15s ease',
        '&:hover': {
            boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
        },
    },
    '& .confirm-file-card-parent': {
        borderColor: '#1565c0',
        backgroundColor: '#f0f4ff',
    },
    '& .confirm-file-children': {
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        marginTop: 8,
        paddingTop: 8,
        borderTop: '1px solid #dce4f5',
    },
    '& .confirm-file-child-row': {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        paddingLeft: 4,
    },
    '& .confirm-file-name-row': {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
    },
    '& .confirm-file-meta': {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '4px 20px',
        marginTop: 8,
        paddingTop: 8,
        borderTop: '1px solid #f5f5f5',
    },
    '& .confirm-file-meta-item': {
        display: 'flex',
        alignItems: 'center',
        gap: 4,
    },
});

function formatSize(bytes: number): string {
    if (bytes < 1024) {
        return `${bytes} B`;
    }
    if (bytes < 1024 * 1024) {
        return `${(bytes / 1024).toFixed(1)} KB`;
    }
    if (bytes < 1024 * 1024 * 1024) {
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    }
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function formatDate(dateStr: string): string {
    return new Date(dateStr).toLocaleString();
}

function totalSize(files: IWizardFile[]): number | null {
    const sizes = files.map((f) => f.size).filter((s): s is number => s !== undefined);
    if (sizes.length === 0) {
        return null;
    }
    return sizes.reduce((acc, s) => acc + s, 0);
}

interface IConfirmFileCardProps {
    group: FileGroup;
    checked: boolean;
    onToggle: () => void;
}

function ConfirmFileCard({ group, checked, onToggle }: IConfirmFileCardProps) {
    const { parent, children } = group;
    const isParent = children.length > 0;
    const hasMeta = parent.path !== undefined || parent.lastModified !== undefined || parent.creationDate !== undefined;

    return (
        <Box className={`confirm-file-card${isParent ? ' confirm-file-card-parent' : ''}`} sx={{ opacity: checked ? 1 : 0.5 }}>
            <Box className='confirm-file-name-row'>
                <Checkbox size='small' checked={checked} onChange={onToggle} sx={{ p: 0.5, mr: 0.5 }} />
                <InsertDriveFileOutlinedIcon sx={{ fontSize: 18, color: isParent ? '#1565c0' : '#9e9e9e', flexShrink: 0 }} />
                <Typography
                    variant='body2'
                    sx={{ fontWeight: 600, color: '#212121', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                    {parent.name}
                </Typography>
                {isParent && (
                    <Typography variant='caption' sx={{ color: '#1565c0', fontWeight: 600, flexShrink: 0, mr: 1 }}>
                        +{children.length} {children.length === 1 ? 'file' : 'files'}
                    </Typography>
                )}
                {parent.size !== undefined && (
                    <Typography variant='caption' sx={{ color: '#9e9e9e', flexShrink: 0 }}>
                        {formatSize(parent.size)}
                    </Typography>
                )}
            </Box>

            {hasMeta && (
                <Box className='confirm-file-meta'>
                    {parent.path !== undefined && (
                        <Box className='confirm-file-meta-item'>
                            <FolderOutlinedIcon sx={{ fontSize: 13, color: '#bdbdbd' }} />
                            <Typography
                                variant='caption'
                                sx={{ color: '#757575', maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                            >
                                {parent.path}
                            </Typography>
                        </Box>
                    )}
                    {parent.lastModified !== undefined && (
                        <Box className='confirm-file-meta-item'>
                            <AccessTimeOutlinedIcon sx={{ fontSize: 13, color: '#bdbdbd' }} />
                            <Typography variant='caption' sx={{ color: '#757575' }}>
                                Modified: {formatDate(parent.lastModified)}
                            </Typography>
                        </Box>
                    )}
                    {parent.creationDate !== undefined && (
                        <Box className='confirm-file-meta-item'>
                            <CalendarTodayOutlinedIcon sx={{ fontSize: 13, color: '#bdbdbd' }} />
                            <Typography variant='caption' sx={{ color: '#757575' }}>
                                Created: {formatDate(parent.creationDate)}
                            </Typography>
                        </Box>
                    )}
                </Box>
            )}

            {isParent && (
                <Box className='confirm-file-children'>
                    {children.map((child, index) => (
                        <Box key={child.path ?? child.name + index} className='confirm-file-child-row'>
                            <InsertDriveFileOutlinedIcon sx={{ fontSize: 14, color: '#bdbdbd', flexShrink: 0 }} />
                            <Typography
                                variant='caption'
                                sx={{ color: '#757575', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                            >
                                {child.name}
                            </Typography>
                            {child.size !== undefined && (
                                <Typography variant='caption' sx={{ color: '#bdbdbd', flexShrink: 0 }}>
                                    {formatSize(child.size)}
                                </Typography>
                            )}
                        </Box>
                    ))}
                </Box>
            )}
        </Box>
    );
}

interface IQueueProcessingConfirmFilesProps {
    files: IWizardFile[];
    queueType: QueueType;
    onConfirmedFilesChange?: (files: IWizardFile[]) => void;
}

export function QueueProcessingConfirmFiles({ files, queueType, onConfirmedFilesChange }: IQueueProcessingConfirmFilesProps) {
    const handler = getQueueTypeHandler(queueType);
    const groups = handler.groupFiles(files);
    const total = totalSize(files);
    const hasGroups = groups.some((g) => g.children.length > 0);

    const [checkedSet, setCheckedSet] = useState<Set<number>>(() => new Set(groups.map((_, i) => i)));
    const [filterText, setFilterText] = useState('');

    useEffect(() => {
        const newSet = new Set(groups.map((_, i) => i));
        setCheckedSet(newSet);
    }, [files.length]);

    useEffect(() => {
        const confirmed = groups.filter((_, i) => checkedSet.has(i));
        const confirmedFiles = confirmed.flatMap((g) => [g.parent, ...g.children]);
        onConfirmedFilesChange?.(confirmedFiles);
    }, [checkedSet]);

    const handleToggle = (index: number) => {
        setCheckedSet((prev) => {
            const next = new Set(prev);

            if (next.has(index)) {
                next.delete(index);
            } else {
                next.add(index);
            }

            return next;
        });
    };

    const confirmedGroups = groups.filter((_, i) => checkedSet.has(i));
    const confirmedFiles = confirmedGroups.flatMap((g) => [g.parent, ...g.children]);
    const confirmedTotal = totalSize(confirmedFiles);

    const lowerFilter = filterText.toLowerCase();
    const filteredGroups = filterText
        ? groups.map((g, i) => ({ group: g, index: i })).filter(({ group }) => group.parent.name.toLowerCase().includes(lowerFilter))
        : groups.map((g, i) => ({ group: g, index: i }));

    if (groups.length === 0) {
        return (
            <Box css={containerStyle}>
                <Alert severity='warning'>
                    No valid files selected. {handler.mainExtensionsToGroup !== null && `At least one ${handler.mainExtensionsToGroup} file is required.`}
                </Alert>
            </Box>
        );
    }

    return (
        <Box css={containerStyle}>
            <Box className='confirm-summary'>
                {hasGroups && (
                    <Box className='confirm-summary-stat'>
                        <Typography variant='h6' sx={{ fontWeight: 700, color: '#1565c0', lineHeight: 1 }}>
                            {groups.length}
                        </Typography>
                        <Typography variant='caption' sx={{ color: '#9e9e9e' }}>
                            {groups.length === 1 ? 'item' : 'items'}
                        </Typography>
                    </Box>
                )}
                <Box className='confirm-summary-stat'>
                    <Typography variant='h6' sx={{ fontWeight: 700, color: '#1565c0', lineHeight: 1 }}>
                        {files.length}
                    </Typography>
                    <Typography variant='caption' sx={{ color: '#9e9e9e' }}>
                        {files.length === 1 ? 'file' : 'files'} selected
                    </Typography>
                </Box>
                {total !== null && (
                    <Box className='confirm-summary-stat'>
                        <Typography variant='h6' sx={{ fontWeight: 700, color: '#1565c0', lineHeight: 1 }}>
                            {formatSize(total)}
                        </Typography>
                        <Typography variant='caption' sx={{ color: '#9e9e9e' }}>
                            total size
                        </Typography>
                    </Box>
                )}
                <Box sx={{ ml: 'auto', display: 'flex', gap: 3, alignItems: 'center' }}>
                    <Box className='confirm-summary-stat'>
                        <Typography variant='h6' sx={{ fontWeight: 700, color: '#2e7d32', lineHeight: 1 }}>
                            {confirmedGroups.length}
                        </Typography>
                        <Typography variant='caption' sx={{ color: '#9e9e9e' }}>
                            confirmed
                        </Typography>
                    </Box>
                    {confirmedTotal !== null && (
                        <Box className='confirm-summary-stat'>
                            <Typography variant='h6' sx={{ fontWeight: 700, color: '#2e7d32', lineHeight: 1 }}>
                                {formatSize(confirmedTotal)}
                            </Typography>
                            <Typography variant='caption' sx={{ color: '#9e9e9e' }}>
                                confirmed size
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Box>

            <TextField
                size='small'
                placeholder='Filter files...'
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position='start'>
                            <SearchIcon sx={{ fontSize: 18, color: '#9e9e9e' }} />
                        </InputAdornment>
                    ),
                }}
                sx={{ flexShrink: 0, '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: 13 } }}
            />

            <Box className='confirm-list'>
                {filteredGroups.map(({ group, index }) => (
                    <ConfirmFileCard
                        key={group.parent.path ?? group.parent.name + index}
                        group={group}
                        checked={checkedSet.has(index)}
                        onToggle={() => handleToggle(index)}
                    />
                ))}
            </Box>
        </Box>
    );
}
